import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'

import {
  collection,
  doc,
  updateDoc,
  arrayUnion,
  getDoc,
  arrayRemove,
} from 'firebase/firestore'
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from 'firebase/storage'

import { firestore, storage } from '../firebase/firebaseConfig'
import { getFileByCategory } from '../firebase/utils/storageActions'
import { AudioRef, Invoice } from '../types'

interface FirebaseStorageProps {
  uploadFile: (
    file: File,
    userId: string,
    year: number,
    month: string,
  ) => Promise<void>

  deleteFile: (path: string, userId: string) => Promise<void>
  deleteUserFolder: (userUID: string) => Promise<void>
  invoicesList: Invoice[] | null
  getInvoiceList: (userId: string) => Promise<void>
  error: string | null
  loadingStorage: boolean
  progressBar: number | null
  setProgessBar: Dispatch<SetStateAction<number | null>>
  uploadImage: (
    userUID: string,
    blob: Blob,
    collection?: string,
  ) => Promise<string>
  audiosLists: Record<string, { title: string; url: string }[]>[] | null
  getAudioList: () => Promise<void>
  uploadAudio: (
    file: File,
    userId: string,
    logUID: string,
    numeroOrCb?: number | string | ((percent: number) => void),
    maybeCb?: (percent: number) => void,
  ) => Promise<{ url: string; fullPath: string }>
  deleteAudio: (
    userId: string,
    logUID: string,
    pathOrUrl?: string,
  ) => Promise<{ state: boolean; message: string }>
  deleteAudiosFromLog: (studentId: string, logUID: string) => Promise<void>
}

const StorageContext = createContext<FirebaseStorageProps>({
  uploadFile: () => Promise.resolve(),

  deleteFile: () => Promise.resolve(),
  deleteUserFolder: () => Promise.resolve(),
  invoicesList: null,
  getInvoiceList: () => Promise.resolve(),
  error: null,
  loadingStorage: false,
  progressBar: null,
  setProgessBar: () => null,
  uploadImage: () => Promise.resolve(''),
  audiosLists: null,
  getAudioList: () => Promise.resolve(),
  uploadAudio: () => Promise.resolve({ url: '', fullPath: '' }),
  deleteAudio: () => Promise.resolve({ state: false, message: '' }),
  deleteAudiosFromLog: () => Promise.resolve(),
})

export const useStorage = () => useContext(StorageContext)

export const StorageProvider = ({ children }: { children: ReactNode }) => {
  const [loadingStorage, setLoadingStorage] = useState<boolean>(false)
  const [progressBar, setProgessBar] = useState<null | number>(null)
  const [error, setError] = useState<null | string>(null)
  const [invoicesList, setInvoicesList] = useState<Invoice[] | null>(null)
  const [audiosLists, setAudioLists] = useState<
    Record<string, { title: string; url: string }[]>[] | null
  >(null)

  const withLoading = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      setLoadingStorage(true)
      try {
        return await fn()
      } finally {
        setLoadingStorage(false)
      }
    },
    [],
  )

  const uploadFile = async (
    file: File,
    userId: string,
    year: number,
    month: string,
  ) => {
    const fileRef = ref(storage, `${userId}/nominas/${year}/${month}`)

    // const uploadedFile = await uploadBytes(fileRef, file)
    const uploadTask = uploadBytesResumable(fileRef, file)

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        setProgessBar(progress)
      },
      (error) => {
        console.error('Error al subir el archivo:', error)
        setError(`Error al subir el archivo:', ${error}`)
        return `Error al subir el archivo:', ${error}`
      },
      () => {
        getInvoiceList(userId)
      },
    )
  }

  const deleteFile = useCallback(async (path: string, userId: string) => {
    try {
      const fileRef = ref(storage, path)
      await deleteObject(fileRef)
      await getInvoiceList(userId)
    } catch (err) {
      console.error('Error al borrar el archivo:', err)
      setError(`Error al borrar el archivo: ${(err as Error).message}`)
    }
  }, [])

  const uploadAudio = async (
    file: File,
    userId: string,
    logUID: string,
    numeroOrCb?: number | string | ((percent: number) => void),
    maybeCb?: (percent: number) => void,
  ): Promise<{ url: string; fullPath: string }> => {
    setLoadingStorage(true)

    let numero: number | string | undefined
    let onProgress: ((p: number) => void) | undefined

    if (typeof numeroOrCb === 'function') {
      onProgress = numeroOrCb
    } else {
      numero = numeroOrCb
      onProgress = maybeCb
    }

    const fileName = file.name
    const studentRef = doc(firestore, 'students', userId)
    const logsCollectionRef = collection(studentRef, 'logs')
    const logDocRef = doc(logsCollectionRef, logUID)

    const suffix = numero ?? Date.now()
    const fileRef = ref(storage, `${userId}/logs/${logUID}-${suffix}`)

    const uploadTask = uploadBytesResumable(fileRef, file)

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          setProgessBar(progress)
          if (onProgress) onProgress(progress)
        },
        (error) => {
          console.error('Upload failed', error)
          setError(`Error al subir el archivo: ${error.message}`)
          setLoadingStorage(false)
          reject(error) // Rechaza la promesa con el error
        },
        async () => {
          try {
            const audioUrl = await getDownloadURL(uploadTask.snapshot.ref)
            const fullPath = uploadTask.snapshot.ref.fullPath

            await updateDoc(logDocRef, {
              audio: arrayUnion({
                title: fileName,
                url: audioUrl,
                path: fullPath,
              }),
            })

            setError(null) // Restablece cualquier error anterior
            setError('Se ha cargado el archivo')
            resolve({ url: audioUrl, fullPath })
          } catch (error) {
            console.error('Error al obtener la URL de descarga', error)
            setError(`Error inesperado: ${(error as Error).message}`)
            reject(`Error inesperado: ${(error as Error).message}`) // Rechaza la promesa con el error
          } finally {
            setLoadingStorage(false)
          }
        },
      )
    })
  }

  const deleteAudio = async (
    userId: string,
    logUID: string,
    pathOrUrl?: string,
  ): Promise<{ state: boolean; message: string }> => {
    setLoadingStorage(true)
    const studentRef = doc(firestore, 'students', userId)
    const logDocRef = doc(collection(studentRef, 'logs'), logUID)

    return new Promise(async (resolve, reject) => {
      try {
        // Si no se pasa pathOrUrl, limpiamos el campo audio del documento
        if (!pathOrUrl) {
          await updateDoc(logDocRef, { audio: [] })
          setError('Se ha eliminado el archivo')
          resolve({ state: false, message: 'No hay archivo' })
          return
        }

        // 1) BORRAR DEL STORAGE (usamos la ruta/path que guardaste en audio.path o audio.url)
        const targetRef = ref(storage, pathOrUrl)
        await deleteObject(targetRef)
        console.log('audio deleted from storage')

        // 2) BORRAR DEL DOCUMENTO (array audio en Firestore)
        const logSnap = await getDoc(logDocRef)
        const current: AudioRef[] = (logSnap.data()?.audio as AudioRef[]) || []

        if (current.length === 0) {
          setError('No hay archivo')
          resolve({ state: false, message: 'No hay archivo' })
          return
        }

        const toDelete = current.find(
          (item) => item.path === pathOrUrl || item.url === pathOrUrl,
        )

        if (!toDelete) {
          // No se encontró una entrada que matchee; simplemente devolvemos estado actual
          setError('')
          resolve({
            state: current.length > 0,
            message: current.length > 0 ? '' : 'No hay archivo',
          })
          return
        }

        await updateDoc(logDocRef, { audio: arrayRemove(toDelete) })

        const remaining = current.filter((a) => a !== toDelete)

        setError(remaining.length > 0 ? '' : 'No hay archivo')
        resolve({
          state: remaining.length > 0,
          message: remaining.length > 0 ? '' : 'No hay archivo',
        })
      } catch (error) {
        console.error(`Error al borrar el archivo: ${error}`)
        setError(`Error al borrar el archivo: ${error}`)
        reject({ state: true, message: 'Error al eliminar el archivo' })
      } finally {
        setLoadingStorage(false)
        setProgessBar(0)
      }
    })
  }

  const uploadImage = async (
    refId: string,
    blob: Blob,
    collection?: string,
  ): Promise<string> => {
    setLoadingStorage(true)
    let storageRef
    if (collection) {
      storageRef = ref(storage, `${collection}/${refId}.jpeg`)
    } else {
      storageRef = ref(storage, `${refId}/profile-image.jpeg`)
    }

    const uploadTask = uploadBytesResumable(storageRef, blob)

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        null, // Aquí puedes añadir la lógica para el progreso si lo deseas
        (error) => {
          console.error('Upload failed', error)
          setError(`Error al subir el archivo: ${error.message}`)
          setLoadingStorage(false)
          reject(error) // Rechaza la promesa con el error
        },
        async () => {
          try {
            const imageUrl = await getDownloadURL(uploadTask.snapshot.ref)

            setError(null) // Restablece cualquier error anterior
            resolve(imageUrl) // Resuelve la promesa con el fileReference
          } catch (error) {
            console.error('Error al obtener la URL de descarga', error)
            setError(`Error inesperado: ${(error as Error).message}`)
            reject(`Error inesperado: ${(error as Error).message}`) // Rechaza la promesa con el error
          } finally {
            setLoadingStorage(false)
          }
        },
      )
    })
  }

  const getAudioList = useCallback(async () => {
    await withLoading(async () => {
      try {
        const larFolderRef = ref(storage, `/estimulación-laríngea`)
        const resFolderRef = ref(storage, `/trabajo-respiratorio`)
        const resonanFolderRef = ref(storage, `/trabajo-resonancial`)

        const [_larFiles, _resFiles, _resonanFiles] = await Promise.all([
          listAll(larFolderRef),
          listAll(resFolderRef),
          listAll(resonanFolderRef),
        ])

        const [larFiles, resFiles, resonanFiles] = await Promise.all([
          getFileByCategory(_larFiles),
          getFileByCategory(_resFiles),
          getFileByCategory(_resonanFiles),
        ])

        setAudioLists([larFiles, resFiles, resonanFiles])
      } catch (err) {
        console.error('Error al obtener los audios:', err)
        setError(`Error al obtener los audios: ${(err as Error).message}`)
      }
    })
  }, [withLoading, getFileByCategory])

  const getInvoiceList = useCallback(
    async (userId: string) => {
      await withLoading(async () => {
        try {
          const nominasRef = ref(storage, `${userId}/nominas`)
          const yearFolders = await listAll(nominasRef)

          const newFilesByYear: Invoice[] = [] // asumiendo que Invoice tiene {year, months}

          for (const yearFolder of yearFolders.prefixes) {
            const year = Number(yearFolder.name)
            const files = await listAll(yearFolder)

            const months = await Promise.all(
              files.items.map(async (file) => ({
                month: file.name,
                url: await getDownloadURL(file),
                path: file.fullPath,
              })),
            )

            newFilesByYear.push({ year, months } as unknown as Invoice)
          }

          setInvoicesList(newFilesByYear)
        } catch (err) {
          console.error('Error al obtener los archivos:', err)
          setError(`Error al obtener los archivos: ${(err as Error).message}`)
        }
      })
    },
    [withLoading],
  )

  const deleteUserFolder = useCallback(
    async (folderPath: string): Promise<void> => {
      setLoadingStorage(true)
      try {
        const folderRef = ref(storage, folderPath)
        const result = await listAll(folderRef)

        const deleteFilePromises = result.items.map((item) =>
          deleteObject(item),
        )
        const deleteFolderPromises = result.prefixes.map((subFolder) =>
          deleteUserFolder(subFolder.fullPath),
        )

        await Promise.all([...deleteFilePromises, ...deleteFolderPromises])

        setError('La carpeta ha sido eliminada exitosamente')
      } catch (err) {
        console.error('Error al eliminar la carpeta:', err)
        setError(`Error al eliminar la carpeta: ${(err as Error).message}`)
        throw err
      } finally {
        setLoadingStorage(false)
      }
    },
    [],
  )

  const deleteAudiosFromLog = useCallback(
    async (studentId: string, logUID: string) => {
      const studentRef = doc(firestore, 'students', studentId)
      const logDocRef = doc(collection(studentRef, 'logs'), logUID)

      try {
        const logSnap = await getDoc(logDocRef)
        const logData = logSnap.data()
        const audio = (logData?.audio as AudioRef[]) || []

        if (Array.isArray(audio) && audio.length > 0) {
          await Promise.all(
            audio.map(async (audioItem) => {
              const pathToDelete = audioItem.path || audioItem.url
              if (!pathToDelete) return

              try {
                const storageRef = ref(storage, pathToDelete)
                await deleteObject(storageRef)
              } catch (err) {
                console.warn(`No se pudo eliminar ${pathToDelete}:`, err)
              }
            }),
          )
        }

        await updateDoc(logDocRef, { audio: [] })
      } catch (err) {
        console.error('Error al eliminar audios del log:', err)
        setError(`Error al eliminar audios del log: ${(err as Error).message}`)
      }
    },
    [],
  )

  return (
    <StorageContext.Provider
      value={{
        uploadFile,
        deleteFile,
        getInvoiceList,
        invoicesList,
        error,
        loadingStorage,
        progressBar,
        setProgessBar,
        uploadImage,
        audiosLists,
        getAudioList,
        uploadAudio,
        deleteAudio,
        deleteUserFolder,
        deleteAudiosFromLog,
      }}
    >
      {children}
    </StorageContext.Provider>
  )
}

// import { getDownloadURL } from 'firebase/storage'

// export const downloadFile = (ref: StorageReference) =>
//   guardedCall(() => getDownloadURL(ref))
// const copyAudioFile = async (
//   userId: string,
//   originalAudio: { title: string; url: string },
//   newLogUID: string,
//   oldUID: string,
// ): Promise<{ title: string; url: string } | null> => {
//   setLoadingStorage(true)
//   try {
//     const originalRef = ref(storage, `${userId}/logs/${oldUID}`)
//     const newRef = ref(storage, `${userId}/logs/${newLogUID}`)

//     const [blob, meta] = await Promise.all([
//       getBlob(originalRef),
//       getMetadata(originalRef),
//     ])

//     await uploadBytes(newRef, blob, { contentType: meta.contentType })
//     const newUrl = await getDownloadURL(newRef)

//     return { title: originalAudio.title, url: newUrl }
//   } catch (err) {
//     console.error('Error al copiar el archivo de audio:', err)
//     setError(`Error al copiar el archivo de audio: ${(err as Error).message}`)
//     return null
//   } finally {
//     setLoadingStorage(false)
//   }
// }

// const getInvoiceList = async (userId: string) => {
//   const nominasRef = ref(storage, `${userId}/nominas`)
//   setLoadingStorage(true)
//   // Recorre las carpetas de años en "nominas"
//   try {
//     const yearFolders = await listAll(nominasRef)
//     const newFilesByYear:
//       | {
//           year: number
//           months: { month: string; url: string; path: string }[]
//         }[]
//       | null = []

//     for (const yearFolder of yearFolders.prefixes) {
//       const year = Number(yearFolder.name)
//       const files = await listAll(yearFolder)

//       const months = await Promise.all(
//         files.items.map(async (file) => ({
//           month: file.name,
//           url: await getDownloadURL(file),
//           path: file.fullPath,
//         })),
//       )

//       newFilesByYear.push({ year, months })
//     }
//     setInvoicesList(newFilesByYear)
//   } catch (error) {
//     console.error('Error al obtener los archivos:', error)
//     setError(`Error al obtener los archivos:', ${error}`)
//   }
//   setLoadingStorage(false)
// }

// const getAudioList = async () => {
//   const larFolderRef = ref(storage, `/estimulación-laríngea`)
//   const resFolderRef = ref(storage, `/trabajo-respiratorio`)
//   const resonanFolderRef = ref(storage, `/trabajo-resonancial`)
//   const _larFiles = await listAll(larFolderRef)
//   const _resFiles = await listAll(resFolderRef)
//   const _resonanFiles = await listAll(resonanFolderRef)

//   const larFiles = await getFiles(_larFiles)
//   const resFiles = await getFiles(_resFiles)
//   const resonanFiles = await getFiles(_resonanFiles)

//   setAudioLists([larFiles, resFiles, resonanFiles])
// }
